import React from 'react';

import { useCreateArreteAnnuel } from '@/hooks';
import { estEncaissementDirect } from '@/utils';
import { useRegieStore } from '@/zustand';

// Persiste entre les remounts du composant (remount à chaque changement de page par le framework).
// Réinitialisé uniquement au reload complet de la page.
let lastCheckedIdentifiant: number | null = null;

/**
 * ArreteAnnuelAutomatiqueCreator
 *
 * Cas 2 de la clôture annuelle (N-1) : toute l'activité ≤ 31/12 N-1 est déjà rattachée à un arrêté
 * périodique → déclenche, une seule fois par sélection de régie, la création de l'arrêté annuel via
 * le hook `useCreateArreteAnnuel` (même toast de succès que la procédure du Cas 1 ; 422 avalé pour
 * l'unicité/la fenêtre arbitrées par le back). Sur succès, `refreshRegie()` recalcule l'état serveur
 * pour débloquer l'encaissement sans reload. Le déclenchement repose sur le signal back
 * `arreteAnnuelCas === 2` (RegieResource). Le Cas 1 (activité résiduelle) est géré par
 * ArreteAnnuelProcedureCreator. Ne rend rien.
 */
const ArreteAnnuelAutomatiqueCreator = () => {
  const regie = useRegieStore((state) => state.regie);
  const refreshRegie = useRegieStore((state) => state.refreshRegie);
  const { createArreteAnnuel } = useCreateArreteAnnuel();

  React.useEffect(() => {
    const identifiant = regie?.identifiant;
    if (!identifiant) return;
    if (lastCheckedIdentifiant === identifiant) return;
    // En mode embedded (encaissement direct), on ne déclenche aucune création automatique.
    if (estEncaissementDirect()) return;

    // Cas 2 = toute l'activité est déjà rattachée à un arrêté périodique → l'arrêté annuel peut
    // être lancé automatiquement. Signal calculé par le back (RegieResource).
    if (!regie.arreteAnnuelRequis || regie.arreteAnnuelCas !== 2) return;

    // Verrou posé avant l'await pour éviter un double déclenchement au remount.
    lastCheckedIdentifiant = identifiant;

    (async () => {
      // Affiche le même toast que le Cas 1 (« L'arrêté annuel {année} a bien été créé. ») et avale
      // le 422 (unicité/fenêtre arbitrées par le back).
      const success = await createArreteAnnuel(
        regie.arreteAnnuelAnnee ?? new Date().getFullYear() - 1,
      );
      // Sur succès, recalcule encaissementPossible / arreteAnnuelRequis côté serveur (débloque l'encaissement).
      if (success) await refreshRegie();
    })();
  }, [
    regie?.identifiant,
    regie?.arreteAnnuelRequis,
    regie?.arreteAnnuelCas,
    regie?.arreteAnnuelAnnee,
    createArreteAnnuel,
    refreshRegie,
  ]);

  return null;
};

export default ArreteAnnuelAutomatiqueCreator;
